import { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { matches } from "@/mocks/matches";
import { getTeam, formatDateCN } from "@/utils";
import type { Match } from "@/types";

const heroImages: Record<string, string> = {
  m2026100901:
    "https://readdy.ai/api/search-image?query=Dramatic%20basketball%20arena%20interior%20at%20night%20with%20glowing%20spotlights%20above%20a%20polished%20hardwood%20court%20and%20blurred%20crowd%2C%20deep%20blue%20and%20warm%20orange%20lighting%2C%20cinematic%20sports%20photography&width=900&height=700&seq=nba-hero-lal-gsw&orientation=landscape",
  m2026100902:
    "https://readdy.ai/api/search-image?query=Moody%20basketball%20arena%20scene%20with%20dramatic%20dark%20blue%20tones%2C%20bright%20spotlights%20hitting%20an%20empty%20court%2C%20blurred%20fans%20in%20stands%2C%20cinematic%20sports%20photography&width=900&height=700&seq=nba-hero-den-okc&orientation=landscape",
  m2026100903:
    "https://readdy.ai/api/search-image?query=Wide%20shot%20of%20a%20packed%20indoor%20basketball%20arena%20with%20glowing%20scoreboard%20and%20spotlights%2C%20warm%20orange%20and%20deep%20teal%20lighting%2C%20energetic%20crowd%2C%20cinematic%20sports%20photography&width=900&height=700&seq=nba-hero-bos-nyk&orientation=landscape",
};

export default function HeroCarousel() {
  const featured: Match[] = matches.filter((m) => m.status === "live");
  const [index, setIndex] = useState(0);

  const next = useCallback(() => setIndex((i) => (i + 1) % featured.length), [featured.length]);
  const prev = () => setIndex((i) => (i - 1 + featured.length) % featured.length);

  useEffect(() => {
    if (featured.length <= 1) return undefined;
    const timer = setInterval(next, 6000);
    return () => clearInterval(timer);
  }, [next, featured.length]);

  const current = featured[index];

  return (
    <section className="relative w-full bg-primary-950 overflow-hidden">
      <div className="absolute inset-0 opacity-30 bg-[radial-gradient(circle_at_20%_20%,oklch(var(--primary-700)),transparent_55%),radial-gradient(circle_at_80%_80%,oklch(var(--accent-800)),transparent_55%)]"></div>
      <div className="relative w-full px-4 md:px-6 py-10 md:py-14 grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 items-center">
        <div className="animate-fade-up">
          <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-accent-500/15 border border-accent-500/30 text-accent-300 text-xs font-label font-medium mb-5">
            <i className="ri-fire-line"></i> 今日共 {matches.filter((m) => m.date === "2026-10-09").length} 场NBA赛事
          </span>
          <h1 className="font-heading text-4xl md:text-6xl font-bold text-background-50 leading-[1.1] tracking-wide">
            NBA<span className="text-accent-500">直播吧</span>
          </h1>
          <p className="mt-4 text-lg md:text-xl font-medium text-primary-100">实时NBA篮球比赛直播平台</p>
          <p className="mt-3 text-sm md:text-base text-primary-300 leading-relaxed max-w-lg">
            汇聚最新NBA比赛直播与赛程信息，涵盖全面的赛事动态，让篮球迷随时掌握比赛实况。
          </p>
          <div className="mt-7 flex flex-wrap gap-3">
            <Link
              to="/live"
              className="inline-flex items-center gap-2 px-6 py-3 rounded-md bg-accent-500 text-accent-950 text-sm font-semibold hover:bg-accent-400 transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-live-line text-lg"></i> 观看直播
            </Link>
            <Link
              to="/schedule"
              className="inline-flex items-center gap-2 px-6 py-3 rounded-md bg-background-50/10 text-background-50 border border-background-50/20 text-sm font-semibold hover:bg-background-50/20 transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-calendar-line text-lg"></i> 查看赛程
            </Link>
          </div>
        </div>

        <div className="relative">
          <div className="relative rounded-xl overflow-hidden border border-primary-800 bg-primary-900">
            {current && (
              <div className="relative h-[280px] md:h-[360px]">
                <img
                  key={current.id}
                  src={heroImages[current.id]}
                  alt={`${getTeam(current.home).name} 对阵 ${getTeam(current.away).name}`}
                  title={`${getTeam(current.home).name} vs ${getTeam(current.away).name} 直播 - NBA直播吧`}
                  className="w-full h-full object-cover object-top animate-fade-up"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-primary-950 via-primary-950/45 to-transparent"></div>
                <div className="absolute top-0 left-0 right-0 flex items-center justify-between p-4">
                  <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-accent-500 text-accent-950 text-xs font-label font-semibold">
                    <span className="w-1.5 h-1.5 rounded-full bg-accent-950 animate-pulse-dot"></span> 正在直播
                  </span>
                  <span className="px-2.5 py-1 rounded-full bg-primary-950/60 text-background-50 text-xs font-label backdrop-blur-sm">
                    {current.stage}
                  </span>
                </div>
                <div className="absolute bottom-0 left-0 right-0 p-4">
                  <h2 key={`${current.id}-t`} className="font-heading text-xl md:text-2xl font-bold text-background-50 leading-tight">
                    {getTeam(current.home).short} <span className="text-accent-400">VS</span> {getTeam(current.away).short}
                  </h2>
                  <div className="mt-3 flex flex-wrap items-center justify-between gap-3">
                    <div className="flex items-center gap-3 bg-background-50/10 backdrop-blur-sm rounded-lg px-3 py-2 border border-background-50/15">
                      <span className="font-heading text-xl font-bold text-accent-400">{current.homeScore}</span>
                      <span className="text-primary-300 font-label text-xs">:</span>
                      <span className="font-heading text-xl font-bold text-accent-400">{current.awayScore}</span>
                      <span className="text-background-50/80 text-xs font-label ml-1">
                        {current.period} {current.clock}
                      </span>
                    </div>
                    <Link
                      to={`/match/${current.id}`}
                      className="inline-flex items-center gap-1.5 px-4 py-2 rounded-md bg-accent-500 text-accent-950 text-xs font-semibold hover:bg-accent-400 transition-colors cursor-pointer whitespace-nowrap"
                    >
                      <i className="ri-play-circle-fill"></i> 进入直播间
                    </Link>
                  </div>
                </div>
              </div>
            )}
            <div className="flex items-center justify-between px-4 py-3 border-t border-primary-800 bg-primary-900/60">
              <button
                type="button"
                onClick={prev}
                aria-label="上一场"
                className="w-8 h-8 rounded-full bg-primary-800 text-background-50 flex items-center justify-center hover:bg-primary-700 cursor-pointer"
              >
                <i className="ri-arrow-left-s-line"></i>
              </button>
              <div className="flex items-center gap-2">
                {featured.map((m, i) => (
                  <button
                    key={m.id}
                    type="button"
                    aria-label={`切换到第 ${i + 1} 场直播`}
                    onClick={() => setIndex(i)}
                    className={`h-1.5 rounded-full transition-all cursor-pointer ${i === index ? "w-7 bg-accent-500" : "w-2.5 bg-primary-600"}`}
                  ></button>
                ))}
              </div>
              <button
                type="button"
                onClick={next}
                aria-label="下一场"
                className="w-8 h-8 rounded-full bg-primary-800 text-background-50 flex items-center justify-center hover:bg-primary-700 cursor-pointer"
              >
                <i className="ri-arrow-right-s-line"></i>
              </button>
            </div>
          </div>
          <p className="mt-3 text-xs text-primary-400 flex items-center gap-1.5">
            <i className="ri-information-line"></i> 焦点直播实时更新，{formatDateCN("2026-10-09")} 赛况以实际为准
          </p>
        </div>
      </div>
    </section>
  );
}